import { BadgeVariant } from '@/components/Badge/Badge.vue';
import { InlineCreateAllocationVariantProps } from '@/components/InlineCreateAllocationVariant/InlineCreateAllocationVariant.vue';
import {
    CreateExternalEventVariantDocument,
    ExternalEventVariantDataFragment,
    ExternalEventWithAllocationExistsDataFragment,
    GetExternalEventVariantsDocument,
    SetCurrentExternalEventVariantDocument,
} from '@/generated/graphql';
import { $t } from '@/plugins/fluent';
import { useMyUser, waitForUser } from '@/user/composables/useMyUser';
import { VariantCardVariant, VariantStatus } from '@/variant/types';
import { useMutation, useQuery } from '@vue/apollo-composable';
import { computed, nextTick, Ref, ref, toValue } from 'vue';
import { ExternalEventVariantCardExpose } from '../components/ExternalEventVariantCard.vue';
import {
    externalEventVariantStatusToBadgeVariant,
    getExternalEventVariantLinkTitle,
    getExternalEventVariantRoute,
} from '../externalEvent';

export function useExternalEventVariants(
    event: Ref<ExternalEventWithAllocationExistsDataFragment | undefined | null>,
    variantCardCompositionRef: Ref<ExternalEventVariantCardExpose[]>,
) {
    const variantsRef: Ref<VariantCardVariant[]> = ref([]);
    const isNational = ref(false);
    const inlineCreateStatus: Ref<InlineCreateAllocationVariantProps['status']> = ref('Default');

    const { currentUser } = useMyUser();

    const setCurrentVariantMutation = useMutation(SetCurrentExternalEventVariantDocument, { fetchPolicy: 'no-cache' });
    const createNewVariantMutation = useMutation(CreateExternalEventVariantDocument);

    const { result, loading, error, onResult } = useQuery(
        GetExternalEventVariantsDocument,
        () => ({
            id: event.value!.id,
        }),
        () => ({ enabled: !!event.value }),
    );

    const category = computed(() => result.value?.externalEvent?.series.category);

    function mapVariantForCard(variant: ExternalEventVariantDataFragment): VariantCardVariant {
        return {
            ...variant,
            status: variant.status as VariantStatus,
            statusBadgeLabel: $t('external-event-variant-status', { status: variant.status }),
            statusBadgeVariant: externalEventVariantStatusToBadgeVariant(variant.status) as BadgeVariant,
            linkTarget: event.value ? getExternalEventVariantRoute(event.value.id, variant.index) : '',
            linkTitle: event.value ? getExternalEventVariantLinkTitle(event.value, variant) : '',
            referenceAllocationId: variant.referenceAllocationId ?? undefined,
        };
    }

    onResult((result) => {
        if (result.error) {
            throw new Error('Error on receiving variants and comments');
        }
        const data = result.data;
        if (data) {
            const variants = data.variants?.nodes;
            if (variants) {
                variantsRef.value = variants.map((variant) => mapVariantForCard(variant));
            }
            isNational.value = data.externalEvent?.series.national ? true : false;
        }
    });

    const currentVariant = computed(() => {
        if (variantsRef.value) {
            const variant = toValue(variantsRef).find((v) => v.isCurrent);
            if (!variant) throw new Error('There must be at least one current variant!');
            return variant;
        }
    });

    function setVariantsRef(value: VariantCardVariant[]) {
        variantsRef.value = value;
    }

    async function setCurrentVariant(variantId: string) {
        const variantsState = variantsRef.value;
        const newVariant = variantsState.find((v) => v.id === variantId);
        if (!newVariant) throw new Error('New current variant does not exist');

        if (currentVariant.value) {
            const result = await setCurrentVariantMutation.mutate({
                oldCurrentId: currentVariant.value.id,
                newCurrentId: newVariant.id,
            });

            if (result?.errors) throw new Error('Could not set variant to current variant');

            const oldVariantResult = result?.data?.updateOld?.variant;
            const newVariantResult = result?.data?.updateNew?.variant;

            if (oldVariantResult && newVariantResult) {
                const updatedVariantsState = variantsState.map((variant) => {
                    if (variant.id === oldVariantResult.id) {
                        return { ...variant, isCurrent: oldVariantResult.isCurrent };
                    }
                    if (variant.id === newVariantResult.id) {
                        return { ...variant, isCurrent: newVariantResult.isCurrent };
                    }
                    return variant;
                });
                setVariantsRef(updatedVariantsState);

                await nextTick();

                // call child functions of the VariantCards that are changed
                const exposeOldVariant = variantCardCompositionRef.value.find((c) => c.id === oldVariantResult.id);
                await exposeOldVariant?.createNewMainVariantEntry(
                    oldVariantResult.id,
                    oldVariantResult.index,
                    newVariantResult.id,
                    newVariantResult.index,
                );
                const exposeNewVariant = variantCardCompositionRef.value.find((c) => c.id === newVariantResult.id);
                await exposeNewVariant?.createNewMainVariantEntry(
                    oldVariantResult.id,
                    oldVariantResult.index,
                    newVariantResult.id,
                    newVariantResult.index,
                );
            }
        }
    }

    const setCurrentVariantError = computed(() => {
        if (!setCurrentVariantMutation.loading && setCurrentVariantMutation.error) {
            return true;
        }
        return false;
    });

    async function createNewInlineVariant() {
        if (event.value) {
            const currentVariantsRef = variantsRef.value;
            inlineCreateStatus.value = 'Pending';
            let result = undefined;
            await waitForUser(currentUser);
            try {
                result = await createNewVariantMutation.mutate({
                    id: event.value.id,
                    name: '',
                    createdById: currentUser.value.id,
                    createdByName: currentUser.value.name,
                });
            } catch (error) {
                inlineCreateStatus.value = 'Error';
                return;
            }
            if (result?.errors) {
                inlineCreateStatus.value = 'Error';
                return;
            }
            if (result?.data?.create?.variant) {
                const newVariant = mapVariantForCard(result.data.create?.variant);
                setVariantsRef([newVariant, ...currentVariantsRef]);
            }
            inlineCreateStatus.value = 'Default';
        }
    }

    return {
        category,
        variantsRef,
        isNational,
        currentVariant,
        loading,
        error,
        setCurrentVariantLoading: setCurrentVariantMutation.loading,
        setCurrentVariantError,
        inlineCreateStatus,
        setVariantsRef,
        setCurrentVariant,
        createNewInlineVariant,
    };
}
